// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getPromotionMessage } from "@mcmc/App/Store/Selectors/ProductDetailsSelectors";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProduct;

const mapStateToProps = (state: McMcApplicationState, props: OwnProps) => ({
    promotion: getPromotionMessage(state, props.product),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsPromotionStyles {
    wrapper?: InjectableCss;
    promoLabel?: TypographyPresentationProps;
    promoMessage?: TypographyPresentationProps;
}

export const promotionStyles: ProductDetailsPromotionStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            margin: 20px 0;
        `,
    },
    promoLabel: {
        size: 14,
        weight: "bold",
        lineHeight: "26px",
        color: "primary.contrast",
        css: css`
            white-space: nowrap;
            margin: 2px 5px 2px 0;
            padding: 0 15px;
            border-radius: 3px;
            background-color: ${getColor("info")};
            ${(props: { bgColor?: string }) => {
                if (props.bgColor) {
                    return css`
                        background-color: ${`#${props.bgColor}`};
                    `;
                }
                return null;
            }}
            letter-spacing: 0;
        `,
    },
    promoMessage: {
        size: 13,
        weight: "bold",
        lineHeight: "15px",
        css: css`
            letter-spacing: 0.2px;
            max-width: 230px;
        `,
    },
};

const styles = promotionStyles;

const XTypography: typeof Typography & React.FC<{ bgColor?: string }> = Typography;

const ProductDetailsPromotion: React.FC<Props> = ({ promotion }) => {
    if (!promotion) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <XTypography bgColor={promotion.color} {...styles.promoLabel}>
                {translate("PROMO")}
            </XTypography>
            <Typography data-test-selector="productDetails_promotion" {...styles.promoMessage}>
                {promotion.message}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsPromotion)),
    definition: {
        displayName: "Promotion Message",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
